import { Modal } from 'react-bootstrap'
import { Controller } from 'react-hook-form'
import { FormInput } from '../../../components/forms/input/FormInput'
import { TUsuarioForm, useUsuarioForm } from '../models/usuarioForm.type'
import { FormSelect } from '../../../components/forms/select/FormSelect'
import {
  useRecuperarOpcCatalogoQuery,
  useRecuperarUsuarioPorIdQuery,
  useActualizarUsuarioMutation,
} from '../../../redux/api'
import { AppCodigosCatalogo } from '../../../utils/Constantes'
import { useCallback, useEffect, useState } from 'react'
import { Opcion } from '../../../models/catalogo.type'
import { OpcionesSitios } from './OpcionesSitios'
import { Usuario, DivisionDeNegocioOpcion, style } from '../models/usuario.type'
import { CargaInfo } from '../../../components/layout/CargaInfo'
import toast, { Toaster } from 'react-hot-toast'
import { alertUsuarios } from '../../../utils/alerts/AlertUsuarios'
import { Cargando } from '../../../components/layout/Cargando'
import { Roles } from '../../../utils/Roles'
import useHasPermiso from '../../../hooks/autenticacion/useHasPermiso'
import { CompararDivisiones } from '../../../utils/CompararDivisiones'
import { Alert } from '../../../components/alerts/Alert'
import { ModalUsuarioHeader } from './ModalUsuarioHeader'
import { ModalUsuarioFooter } from './ModalUsuarioFooter'

type Props = {
  mostrarModal: boolean
  isModoEditar: boolean
  usuarioId: string
  cerrarModal: () => void
  refetchConsulta: () => void
}

/**
 * Modal de alta/edicion de usuario en dos pasos:
 * 1) Datos generales (nombre, correo, telefono, perfil, posicion, divisiones).
 * 2) Seleccion de sitios (OpcionesSitios).
 * - En editar: boton "Actualizar" persiste sin cambiar sitio;
 *   si cambian las divisiones, muestra alerta y redirige a paso 2.
 * - En crear: boton "Siguiente" va al paso 2.
 */
export const ModalAgregarEditarUsuario = ({
  mostrarModal,
  isModoEditar,
  cerrarModal,
  usuarioId,
  refetchConsulta,
}: Props) => {
  const { hasPermiso } = useHasPermiso()
  const [showAlert, setShowAlert] = useState(<></>)
  const usuarioPermiso = hasPermiso([Roles.roleUsuariosCrear])
  const formUsuario = useUsuarioForm()
  const [usuarioCargado, setUsuarioCargado] = useState(false)
  const [mostrarSelecSitios, setMostrarSelecSitios] = useState(false)
  const [usuarioFormulario, setUsuarioFormulario] = useState<Usuario>()
  const [filtroDivisiones, setFiltroDivisiones] = useState<string>('')
  const [checkEstatus, setCheckEstatus] = useState<boolean>()
  const [checkAusente, setCheckAusente] = useState<boolean>()
  const { data: catPosicionesContacto } = useRecuperarOpcCatalogoQuery(
    AppCodigosCatalogo.POSICION_CONTACTO
  )
  const { data: catDivisionesNegocio } = useRecuperarOpcCatalogoQuery(
    AppCodigosCatalogo.DIVISION_NEGOCIO
  )
  const { data: catPerfiles } = useRecuperarOpcCatalogoQuery(AppCodigosCatalogo.PERFILES_USUARIOS)
  const {
    register,
    formState: { errors },
    control,
    handleSubmit,
    reset,
  } = formUsuario

  const {
    data: usuario,
    isSuccess,
    isError,
    isLoading,
    error,
    refetch,
  } = useRecuperarUsuarioPorIdQuery(usuarioId, {
    skip: !isModoEditar,
  })

  const [
    actualizarUsuario,
    { error: errorActualizar, isLoading: isLoadingActualizar, isError: isErrorActualizar },
  ] = useActualizarUsuarioMutation()

  /**
   * @description función para actualizar desde vista del formulario
   * @author andres.martinez, Hábil MX
   * @param usuario
   */
  const actualizar = useCallback(
    (usuario: Usuario) => {
      actualizarUsuario({ ...usuario, estatus: checkEstatus, ausente: checkAusente })
        .unwrap()
        .then(() => {
          toast.success('¡Usuario actualizado!', { style })
          refetch()
          setShowAlert(<></>)
          refetchConsulta()
        })
    },
    [actualizarUsuario, checkAusente, checkEstatus, refetch, refetchConsulta]
  )

  useEffect(() => {
    if (!usuarioCargado && isModoEditar && isSuccess) {
      reset({
        ...usuario,
        divisionesNegocio: usuario.divisionesNegocio.map((dn: DivisionDeNegocioOpcion) => {
          return {
            valor: dn.nombre,
            etiqueta: dn.descripcion,
          }
        }),
        posicion: {
          valor: usuario.posicion,
          etiqueta: usuario.descPosicion,
        },
        perfil: {
          valor: usuario.perfil,
          etiqueta: usuario.descPerfil,
        },
      })
      setUsuarioCargado(true)
      setCheckEstatus(usuario.estatus)
      setCheckAusente(usuario.ausente)
    }
    if (isErrorActualizar) {
      alertUsuarios(isModoEditar, actualizar, errorActualizar)
    }
  }, [actualizar, errorActualizar, isErrorActualizar, isModoEditar, isSuccess, reset, usuario, usuarioCargado])

  /**
   * @description Validación de formulario que permite actualizar un usuario o enviar a selección de sitios
   * @author andres.martinez, Hábil MX
   * @param data Información que se extrae del formulario
   * @param accion Bandera que permite la acción de actualizar un usuario o enviar a selección de sitios
   */
  const onSubmit = (data: TUsuarioForm, accion: string) => {
    const usuarioForm = {
      ...data,
      divisionesNegocio: data.divisionesNegocio?.map((dn: Opcion) => {
        return {
          nombre: dn.valor,
          descripcion: dn.etiqueta,
        }
      }),
      perfil: data.perfil.valor,
      descPerfil: data.perfil.etiqueta,
      posicion: data.posicion.valor,
      descPosicion: data.posicion.etiqueta,
    }
    delete usuarioForm.fechaActualizacion
    delete usuarioForm.fechaCreacion
    isModoEditar && delete usuarioForm.correo
    if (accion === 'actualizar') {
      if (CompararDivisiones(usuario?.divisionesNegocio, usuarioForm.divisionesNegocio)) {
        actualizar(usuarioForm)
      } else {
        setShowAlert(
          <div className='pt-4'>
            <Alert
              mensaje='Para actualizar el usuario, pasa a la siguiente pantalla y comprueba los sitios del usuario.'
              color='dodger-blue'
              border
            />
          </div>
        )
      }
    } else {
      setFiltroDivisiones(
        data.divisionesNegocio
          ?.map((dn: Opcion) => {
            return dn.valor
          })
          .join(',')
      )
      siguiente(usuarioForm)
      setShowAlert(<></>)
    }
  }

  /**
   * @description Función para mostrar selección de sitios al presionar siguiente
   * @author andres.martinez, Hábil MX
   * @param usuario
   */
  const siguiente = (usuario: Usuario) => {
    setMostrarSelecSitios(true)
    setUsuarioFormulario({ ...usuario, estatus: checkEstatus, ausente: checkAusente })
  }

  return (
    <>
      <Toaster />
      <Modal
        show={mostrarModal}
        onHide={() => {
          cerrarModal()
        }}
        dialogClassName={`modal-dialog h-900px ${
          !mostrarSelecSitios ? 'w-650px' : 'mw-1000px'
        } overflow-auto`}
        size='lg'
        scrollable={true}
        backdrop='static'
        keyboard={false}>
        {isModoEditar && (isLoading || isError) ? (
          <>
            <div className='d-flex justify-content-end p-7'>
              <button
                onClick={cerrarModal}
                className=' close btn btn-outline-dark btn-icon btn-circle btn-sm ms-5 d-flex col-12 '>
                <i className='fa-solid fa-xmark fs-1'></i>
              </button>
            </div>
            <CargaInfo
              mensaje='Recuperando información del usuario'
              isError={isError}
              mensajeError='Ocurrió un error al recuperar los datos del usuario'
              errorApi={error}
            />
          </>
        ) : (
          <>
            <ModalUsuarioHeader
              usuario={usuario}
              isModoEditar={isModoEditar}
              cerrarModal={cerrarModal}></ModalUsuarioHeader>
            {!mostrarSelecSitios ? (
              <>
                <Modal.Body
                  className={`${isLoadingActualizar && 'overlay overlay-block'} ${
                    usuarioPermiso ? '' : 'overlay overlay-allowed'
                  }`}>
                  <form id='form-usuario'>
                    <div>
                      <div className='row'>
                        <div className='col-12 mb-4 text-center'>
                          <h3>Datos generales</h3>
                        </div>
                        <div className='col-12'>
                          <FormInput
                            className='mb-5'
                            nombre='Nombre(s)'
                            nombreCampo='nombre'
                            isObligatorio={true}
                            register={register}
                            errors={errors}
                            placeholder='Ej. Ricardo'
                            maxLength={50}
                            disabled={!usuarioPermiso}
                          />
                        </div>
                        <div className='col-6'>
                          <FormInput
                            className='mb-5'
                            nombre='Primer apellido'
                            nombreCampo='apellidoPaterno'
                            isObligatorio={true}
                            register={register}
                            errors={errors}
                            placeholder='Ej. Ayala'
                            maxLength={50}
                            disabled={!usuarioPermiso}
                          />
                        </div>
                        <div className='col-6'>
                          <FormInput
                            className='mb-5'
                            nombre='Segundo apellido'
                            nombreCampo='apellidoMaterno'
                            isObligatorio={false}
                            register={register}
                            errors={errors}
                            placeholder='Ej. Ayala'
                            maxLength={50}
                            disabled={!usuarioPermiso}
                          />
                        </div>
                        <div className='col-6'>
                          <FormInput
                            className='mb-5'
                            nombre='Correo electrónico'
                            nombreCampo='correo'
                            isObligatorio={true}
                            register={register}
                            errors={errors}
                            placeholder='Ej. ricardo@veolia.com'
                            maxLength={50}
                            tipo={'email'}
                            disabled={isModoEditar || !usuarioPermiso}
                          />
                        </div>
                        <div className='col-6'>
                          <FormInput
                            className='mb-5'
                            nombre='Teléfono'
                            nombreCampo='telefono'
                            isObligatorio={false}
                            register={register}
                            errors={errors}
                            placeholder='Ej. 5585992181'
                            maxLength={16}
                            disabled={!usuarioPermiso}
                          />
                        </div>
                        <div className='col-7'>
                          <Controller
                            control={control}
                            name='posicion'
                            render={({ field: { onChange, value } }) => (
                              <FormSelect
                                nombre='Posición'
                                nombreCampo='posicion'
                                value={value}
                                isObligatorio={true}
                                errors={errors}
                                elementos={catPosicionesContacto}
                                onChange={(e: Event) => {
                                  onChange(e)
                                }}
                                placeholder='Ej. Jefe de calidad'
                                isDisabled={!usuarioPermiso}
                              />
                            )}
                          />
                        </div>
                        <div className='col-5'>
                          <Controller
                            control={control}
                            name='perfil'
                            render={({ field: { onChange, value } }) => (
                              <FormSelect
                                nombre='Perfil'
                                nombreCampo='perfil'
                                value={value}
                                isObligatorio={true}
                                errors={errors}
                                elementos={catPerfiles}
                                onChange={(e: Event) => {
                                  onChange(e)
                                }}
                                placeholder='Ej. Coordinador'
                                isDisabled={!usuarioPermiso}
                              />
                            )}
                          />
                        </div>
                        <div className='col-12 mt-4'>
                          <Controller
                            control={control}
                            name='divisionesNegocio'
                            render={({ field: { onChange, value } }) => (
                              <FormSelect
                                isMulti
                                nombre='División de negocio'
                                nombreCampo='divisionesNegocio'
                                value={value}
                                isObligatorio={true}
                                errors={errors}
                                elementos={catDivisionesNegocio}
                                onChange={(e: Event) => {
                                  onChange(e)
                                }}
                                placeholder='Ej. Industrial'
                                isDisabled={!usuarioPermiso}
                              />
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </form>
                  {showAlert}
                  {isModoEditar && (
                    <>
                      <div className='separator my-5' />
                      <div className='d-flex flex-stack'>
                        <div className='d-flex flex-column'>
                          <div className='fs-5 text-dark text-hover-primary fw-bold'>Ausente</div>
                          <div className='f-6 fw-semibold text-gray-400'>
                            Establece si el usuario estará ausente
                          </div>
                        </div>
                        <div className='d-flex justify-content-end'>
                          <label className='form-check form-switch form-check-custom form-check-solid form-check-success'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              value='1'
                              defaultChecked={usuario?.ausente && usuario.estatus}
                              onChange={(e) => {
                                setCheckAusente(e.target.checked)
                              }}
                              disabled={!usuarioPermiso}
                            />
                          </label>
                        </div>
                      </div>
                      <div className='separator separator-dashed my-5' />
                      <div className='d-flex flex-stack'>
                        <div className='d-flex flex-column'>
                          <div className='fs-5 text-dark text-hover-primary fw-bold'>Estatus</div>
                          <div className='f-6 fw-semibold text-gray-400'>
                            Establece el estado del usuario
                          </div>
                        </div>
                        <div className='d-flex justify-content-end'>
                          <label className='form-check form-switch form-check-custom form-check-solid form-check-success'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              value='1'
                              defaultChecked={usuario?.estatus}
                              onChange={(e) => {
                                setCheckEstatus(e.target.checked)
                              }}
                              disabled={!usuarioPermiso}
                            />
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                  {isLoadingActualizar && <Cargando mensaje='Guardando usuario' />}
                </Modal.Body>

                <ModalUsuarioFooter
                  isModoEditar={isModoEditar}
                  isLoadingActualizar={isLoadingActualizar}
                  cerrarModal={cerrarModal}
                  handleSubmit={handleSubmit}
                  onSubmit={onSubmit}></ModalUsuarioFooter>
              </>
            ) : (
              <OpcionesSitios
                usuarioSitios={usuario?.sitios!}
                isModoEditar={isModoEditar}
                formulario={usuarioFormulario}
                cerrarModal={cerrarModal}
                regresar={(mostrarSelecSitios: boolean) => {
                  setMostrarSelecSitios(mostrarSelecSitios)
                }}
                refetch={refetch}
                filtroDivisionesNegocio={filtroDivisiones}
                refetchConsulta={refetchConsulta}
              />
            )}
          </>
        )}
      </Modal>
    </>
  )
}
